/**
 * Phase 4b: Rust-backed WorksheetCompilerBackend.
 *
 * Ships the first real end-to-end production path that drives the compiled
 * `fusegrid_v2_wasm_nuclear` module through the adapter seam.
 *
 * Surface rules (do not relax without bumping
 * `WORKSHEET_COMPILER_CONTRACT_VERSION`):
 *   - This module is the *only* production consumer of `compileWorksheetJson`
 *     from the WASM bundle. Call sites should go through the adapter (which
 *     calls this via `setWorksheetCompilerBackend`), never directly.
 *   - We JSON-serialize every argument. The WASM boundary is string-in /
 *     string-out. No `serde-wasm-bindgen` glue. No BigInt or Map across the
 *     boundary. This matches `wasm/src/worksheet_compiler.rs` and is
 *     explicitly tested in the Rust-parity tests.
 *   - Any input the Rust compiler cannot lower (missing typed spec,
 *     unsupported feature, non-matching contract version) throws
 *     `WorksheetCompilerNotImplementedError`. The adapter does not catch
 *     that as a fallback; production strictness treats it as a hard compile
 *     failure to keep TS legacy out of the live path.
 *   - The WASM module is initialized lazily once per process. Consumers
 *     wanting to preload (worker startup, conformance harness) call
 *     `initRustWorksheetCompilerBackendAsync` / `*Sync` explicitly.
 */
import type { WorksheetQueryExecution } from './worksheetQueryArtifacts'
import type { TypedCalcLiteral, TypedWorksheetSpec } from './worksheetCompilerTyped'
import type {
  CompileWorksheetExecutionInput,
  WorksheetCompilerBackend,
} from './worksheetCompilerAdapter'
import { WORKSHEET_COMPILER_CONTRACT_VERSION } from './worksheetCompilerAdapter'
import { coerceBoundParametersForRust } from './buildWorksheetCompilerInput'

/**
 * Optional envelope fields the adapter input can attach alongside a typed
 * spec. These are *not* authoritative — when a full
 * `PreparedWorksheetExecutionCore` is available we derive the envelope from
 * it. This shape is a hatch for callers/tests that want to drive the Rust
 * compiler directly without building a full prepared core.
 */
export type TypedSpecEnvelopeOverrides = {
  authoringRevisionId?: string | null
  bindingGeneration?: string | null
  extractUri?: string | null
  baseFromSql?: string | null
  extractColumns?: string[] | null
  parameters?: Record<string, any>
  requestFilters?: any[]
  worksheetName?: string | null
}

/**
 * The adapter-level input shape with the Phase 4 extension field. Exported
 * so tests and harness code can construct it in a type-safe way.
 */
export type RustCompileInput = CompileWorksheetExecutionInput & {
  typedSpec?: TypedWorksheetSpec | null
  typedEnvelope?: TypedSpecEnvelopeOverrides | null
  /**
   * Phase 8: request-time overrides for `typedSpec.parameters`. Each entry
   * overrides the declared default of a parameter with the same name.
   * Passed through to the Rust compiler as `boundParameters` on
   * `CompileWorksheetRequest` and inlined into SQL at compile time. Unknown
   * names are rejected by the Rust validator so typos fail loudly.
   */
  boundParameters?: Record<string, TypedCalcLiteral> | null
}

/**
 * Thrown when the Rust compiler backend cannot (yet) handle the given input.
 *
 * This is NOT an internal compiler error — it's a structured compiler
 * coverage error. The adapter lets it propagate; live callers must fix the
 * typed lowering or Rust compiler support instead of falling back to TS
 * legacy.
 */
export class WorksheetCompilerNotImplementedError extends Error {
  readonly code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'WorksheetCompilerNotImplementedError'
    this.code = code
  }
}

/**
 * Structured error from the Rust compiler. `kind` mirrors the
 * `CompileError` tag in `query_builder/src/worksheet_compiler/response.rs`.
 */
export class WorksheetCompilerRustError extends Error {
  readonly kind: string
  readonly rawPayload: string
  constructor(kind: string, message: string, rawPayload: string) {
    super(message)
    this.name = 'WorksheetCompilerRustError'
    this.kind = kind
    this.rawPayload = rawPayload
  }
}

type WasmModule = {
  compileWorksheetJson(requestJson: string): string
  worksheetCompilerContractVersion(): number
  // Phase 5a: identity-helper parity probe. Optional because existing
  // callers (worker, tests) predating Phase 5a may install a module
  // without this export. Callers that need it must check presence.
  debugIdentityParityJson?(requestJson: string): string
}

let cachedWasm: WasmModule | null = null

/**
 * Install a pre-initialized WASM module instance. Used by tests (Node env)
 * and in-browser worker bootstraps that already hold a ready module.
 *
 * Throws if contract versions don't line up — we refuse to proceed with
 * stale bindings. The caller should rebuild the bundle (`npm run
 * build:wasm`) and retry.
 */
export function installRustWorksheetCompilerWasm(mod: WasmModule): void {
  const runtimeVersion = mod.worksheetCompilerContractVersion()
  if (runtimeVersion !== WORKSHEET_COMPILER_CONTRACT_VERSION) {
    throw new Error(
      `Rust worksheet compiler contract version mismatch: wasm=${runtimeVersion}, ts=${WORKSHEET_COMPILER_CONTRACT_VERSION}. Rebuild the WASM bundle.`,
    )
  }
  cachedWasm = mod
}

export function resetRustWorksheetCompilerWasm(): void {
  cachedWasm = null
}

function getWasm(): WasmModule {
  if (!cachedWasm) {
    throw new WorksheetCompilerNotImplementedError(
      'wasm_not_initialized',
      'Rust worksheet compiler WASM module has not been initialized. Call initRustWorksheetCompilerBackendAsync or installRustWorksheetCompilerWasm first.',
    )
  }
  return cachedWasm
}

/**
 * Lazily initialize the WASM bundle in a browser / worker / Node context.
 *
 * Accepts:
 *   * `undefined` - resolves the bundled wasm URL relative to the JS module
 *     (browser / Vite / worker).
 *   * a `URL | string | Request | Response | Promise<Response>` accepted by
 *     `wasm-bindgen`'s generated init.
 *   * raw bytes (`Uint8Array | ArrayBuffer`) for Node tests.
 */
export async function initRustWorksheetCompilerBackendAsync(
  initArg?: any,
): Promise<void> {
  if (cachedWasm) return
  const bundle: any = await import('fusegrid_v2_wasm_nuclear' as any)
  const init = bundle?.default ?? bundle?.__wbg_init ?? bundle
  if (typeof init === 'function') {
    await init(initArg)
  }
  if (
    typeof bundle?.compileWorksheetJson !== 'function' ||
    typeof bundle?.worksheetCompilerContractVersion !== 'function'
  ) {
    throw new Error(
      'fusegrid_v2_wasm_nuclear bundle is missing the worksheet compiler exports. Rebuild with `npm run build:wasm`.',
    )
  }
  installRustWorksheetCompilerWasm({
    compileWorksheetJson: (s) => bundle.compileWorksheetJson(s),
    worksheetCompilerContractVersion: () =>
      bundle.worksheetCompilerContractVersion(),
    debugIdentityParityJson:
      typeof bundle?.debugIdentityParityJson === 'function'
        ? (s: string) => bundle.debugIdentityParityJson(s)
        : undefined,
  })
}

/**
 * Synchronous init for Node-style tests where we have the `.wasm` bytes
 * already. Uses the `initSync` export from wasm-bindgen's generated JS.
 */
export function initRustWorksheetCompilerBackendSync(
  bundle: any,
  wasmBytes: Uint8Array | ArrayBuffer,
): void {
  if (cachedWasm) return
  if (typeof bundle?.initSync !== 'function') {
    throw new Error(
      'initRustWorksheetCompilerBackendSync requires a bundle with an `initSync` export (wasm-pack target=web).',
    )
  }
  bundle.initSync({ module: wasmBytes })
  installRustWorksheetCompilerWasm({
    compileWorksheetJson: (s) => bundle.compileWorksheetJson(s),
    worksheetCompilerContractVersion: () =>
      bundle.worksheetCompilerContractVersion(),
    debugIdentityParityJson:
      typeof bundle?.debugIdentityParityJson === 'function'
        ? (s: string) => bundle.debugIdentityParityJson(s)
        : undefined,
  })
}

/**
 * Phase 5a identity-helper parity probe.
 *
 * Drives the Rust `debug_identity_parity_json` function through the WASM
 * boundary. Used by parity tests to assert byte equality against the TS
 * implementations of the same helpers in `datasourceExecutionGraph.ts` /
 * `worksheetAggBaseIdentity.ts`. Not a production surface.
 *
 * Throws `WorksheetCompilerNotImplementedError` if the installed WASM
 * module does not expose the probe (e.g. pre-5a builds); callers should
 * rebuild the WASM bundle.
 */
export function callRustIdentityParityProbe(opJson: string): string {
  const wasm = getWasm()
  if (typeof wasm.debugIdentityParityJson !== 'function') {
    throw new WorksheetCompilerNotImplementedError(
      'probe_not_available',
      'Installed WASM module does not export debugIdentityParityJson. Rebuild with `npm run build:wasm`.',
    )
  }
  return wasm.debugIdentityParityJson(opJson)
}

/* -------------------- Request assembly -------------------- */

/**
 * Rust's `WorksheetRuntimeFilter` is declared with
 * `#[serde(rename_all = "camelCase")]`, so it expects `fieldName`,
 * `filterType`, `isMeasure` on the wire. The TypeScript
 * `WorksheetRuntimeFilter` type (and the majority of authoring call
 * sites) uses snake_case keys (`field_name`, `filter_type`,
 * `is_measure`) — that mismatch has been latent as long as the Rust
 * backend was gated behind a typed-spec rejection. Now that runtime
 * filters flow through the typed path end-to-end, we normalize the
 * wire shape here so the Rust contract holds without forcing every
 * TS filter call site (and the legacy TS compiler, which still
 * consumes the snake_case form) to change shape.
 *
 * The TS keys are kept as-is when they're already camelCase so
 * tests / callers that pre-normalize still serialize cleanly.
 */
function normalizeRuntimeFilterForRust(filter: any): any {
  if (!filter || typeof filter !== 'object') return filter
  const out: Record<string, unknown> = { ...filter }
  const remap: Array<[string, string]> = [
    ['field_name', 'fieldName'],
    ['filter_type', 'filterType'],
    ['is_measure', 'isMeasure'],
  ]
  for (const [snake, camel] of remap) {
    if (snake in out) {
      if (!(camel in out) || out[camel] === undefined) {
        out[camel] = out[snake]
      }
      delete out[snake]
    }
  }
  return out
}

function normalizeRuntimeFiltersForRust(filters: any[] | null | undefined): any[] {
  if (!Array.isArray(filters)) return []
  return filters.map(normalizeRuntimeFilterForRust)
}

function defaultEnvelopeFromInput(
  input: RustCompileInput,
): TypedSpecEnvelopeOverrides {
  const envelope = input.typedEnvelope ?? {}
  const prepared = input.preparedExecutionCore ?? null
  const preparedRequest: any = prepared?.request ?? null
  const options: any = input.queryOptions ?? {}
  return {
    authoringRevisionId:
      envelope.authoringRevisionId ??
      preparedRequest?.authoringState?.authoringRevisionId ??
      null,
    bindingGeneration:
      envelope.bindingGeneration ??
      preparedRequest?.binding?.bindingGeneration ??
      null,
    extractUri: envelope.extractUri ?? options.extractUri ?? null,
    baseFromSql: envelope.baseFromSql ?? options.baseFromSql ?? null,
    extractColumns: envelope.extractColumns ?? options.extractColumns ?? null,
    parameters: envelope.parameters ?? options.parameters ?? {},
    requestFilters: envelope.requestFilters ?? options.filters ?? [],
    worksheetName:
      envelope.worksheetName ??
      preparedRequest?.normalizedRequest?.worksheetName ??
      (input.worksheetSpec as any)?.name ??
      null,
  }
}

function buildRustRequestJson(input: RustCompileInput): string {
  const typedSpec = input.typedSpec
  if (!typedSpec) {
    throw new WorksheetCompilerNotImplementedError(
      'no_typed_spec',
      'Rust compiler backend requires a typedSpec in the adapter input.',
    )
  }
  const envelope = defaultEnvelopeFromInput(input)

  const prepared: any = input.preparedExecutionCore?.request
  const capabilities = prepared?.capabilities ?? {
    platform: 'web',
    browserOnlyExecution: true,
    supportsLocalDatasetCacheWrites: true,
    supportsIpcDirectResults: false,
    supportsConcurrentBackgroundSql: false,
    preferredPagingProfile: 'browser',
  }
  const binding = prepared?.binding ?? {
    mode: 'none',
    workbookId: '',
    backendImportId: '',
    attachedImportId: '',
    executionScopeId: '',
    bindingGeneration: envelope.bindingGeneration ?? 'binding:0',
  }
  const authoringState = prepared?.authoringState ?? {
    workbookIr: {},
    workbookLineage: {
      workbookId: '',
      workbookScopeId: '',
      cloudWorkbookId: '',
    },
    importLineage: {
      executionImportId: '',
      attachedImportId: '',
      restoreArtifactImportId: '',
    },
    authoringRevisionId: envelope.authoringRevisionId,
  }
  const normalizedRequestRaw = prepared?.normalizedRequest ?? {
    worksheetSpec: input.worksheetSpec ?? {},
    worksheetName: envelope.worksheetName,
    filters: envelope.requestFilters ?? [],
    parameters: envelope.parameters ?? {},
  }
  // Remap filter wire keys (see `normalizeRuntimeFilterForRust`) so the
  // Rust `#[serde(rename_all = "camelCase")]` contract on
  // `WorksheetRuntimeFilter` accepts filters authored in snake_case.
  const normalizedRequest = {
    ...normalizedRequestRaw,
    filters: normalizeRuntimeFiltersForRust((normalizedRequestRaw as any)?.filters),
  }
  const executionProfile = prepared?.executionProfile ?? 'creatorInteractive'

  const queryOptions: any = {
    extractUri: envelope.extractUri ?? '',
    baseFromSql: envelope.baseFromSql ?? undefined,
    extractColumns: envelope.extractColumns ?? undefined,
    filters: normalizeRuntimeFiltersForRust(envelope.requestFilters ?? []),
    parameters: envelope.parameters ?? {},
  }

  const emitExecPlan = Boolean(
    input.emitExecPlan ||
      (input.queryOptions as any)?.emitExecPlan ||
      (input.queryOptions as any)?.collectExecPlan ||
      (input.queryOptions as any)?.collect_exec_plan,
  )

  const envelopePayload: Record<string, unknown> = {
    request: {
      authoringState,
      normalizedRequest,
      binding,
      executionProfile,
      capabilities,
    },
    queryOptions,
    typedSpec,
    emitExecPlan,
    collectExecPlan: emitExecPlan,
    callerTag: input.callerTag ?? null,
    contractVersion: WORKSHEET_COMPILER_CONTRACT_VERSION,
  }
  // PR3 — merge dashboard action-state parameter overrides into
  // `boundParameters` so the Rust side has a single channel for
  // "what parameter values does the compiler see?". The explicit
  // boundParameters supplied by the caller WIN on collision; action
  // state is the default layer.
  const mergedBoundParameters: Record<string, unknown> = {
    ...(input.resolvedActionState?.parameters ?? {}),
    ...(input.boundParameters ?? {}),
  }
  if (Object.keys(mergedBoundParameters).length > 0) {
    const declaredParamTypes = new Map(
      (typedSpec.parameters ?? []).map((param) => [param.name, param.ty] as const),
    )
    envelopePayload.boundParameters = coerceBoundParametersForRust(
      mergedBoundParameters,
      declaredParamTypes,
    )
  }
  // PR3 — forward the resolver's normalized action state so Rust's
  // filter-lowering pass can consume a single source of truth. Field is
  // optional and #[serde(default)] on the Rust side, so the wire remains
  // backwards-compatible.
  if (input.resolvedActionState) {
    envelopePayload.resolvedActionState = input.resolvedActionState
  }
  return JSON.stringify(envelopePayload)
}

function parseRustError(raw: string): WorksheetCompilerRustError {
  try {
    const parsed = JSON.parse(raw) as { kind?: string; message?: string }
    const kind = parsed?.kind ?? 'unknown'
    const message = parsed?.message ?? raw
    return new WorksheetCompilerRustError(kind, `Rust compiler error (${kind}): ${message}`, raw)
  } catch {
    return new WorksheetCompilerRustError('unparseable', raw, raw)
  }
}

/* -------------------- Backend object -------------------- */

export function createRustWorksheetCompilerBackend(): WorksheetCompilerBackend {
  return {
    id: 'rust',
    compile(input) {
      const wasm = getWasm()
      const requestJson = buildRustRequestJson(input as RustCompileInput)
      let responseJson: string
      try {
        responseJson = wasm.compileWorksheetJson(requestJson)
      } catch (err: any) {
        if (err instanceof WorksheetCompilerNotImplementedError) throw err
        const raw = typeof err === 'string' ? err : err?.message ?? String(err)
        throw parseRustError(raw)
      }
      let exec: WorksheetQueryExecution
      try {
        exec = JSON.parse(responseJson) as WorksheetQueryExecution
      } catch (err: any) {
        throw new Error(
          `Rust compiler returned non-JSON response: ${err?.message ?? String(err)}`,
        )
      }
      return exec
    },
  }
}
